import { observable } from 'mobx';
import { GoFeralApiClient } from '../../clients/GoFeralApiClient';
import { MyProfileModel } from '../../models/MyProfileModel';
import { v4 } from 'uuid';
import { MeResponse, REFRESH_TOKEN_VERSION } from '@foxbun/lib-goferal-types';
import { GoFeralTokenWrapper } from './GoFeralTokenWrapper';
import { GoFeralAuthClient } from '../../clients/GoFeralAuthClient';
import { Notification, NotificationStore, NotificationType } from '../NotificationStore';
import { BaseObserver } from '@journeyapps-labs/common-utils';
import { GeoLocationEngine } from '../GeoLocationEngine';
import * as t from 'ts-codec';
import { ObjectId } from 'bson';
import * as jwt from 'jsonwebtoken';
import { PackStore } from '../models/PackStore';
import { UserNotificationEngine } from '../UserNotificationEngine';
import { ProfileStore } from '../models/ProfileStore';
import { GroupStore } from '../models/GroupStore';
import { APP_ENV } from '../../../env';

export interface SessionListener {
  logout: () => any;
  activate: () => any;
  deactivate: () => any;
  refreshTokenUpdated: () => any;
  dispose: () => any;
}

export interface SessionSerialized {
  refresh_token: string;
  profile_id: string;
  session_id: string;
  profile: t.Encoded<typeof MeResponse> | null;
}

export interface SessionOptions {
  auth_client: GoFeralAuthClient;
  notification_store: NotificationStore;
  profile_id: ObjectId;
}

export class Session extends BaseObserver<SessionListener> {
  refresh_token: string;
  id: string;

  token_wrapper: GoFeralTokenWrapper;
  api_client: GoFeralApiClient;

  @observable
  accessor user: MyProfileModel;

  geoLocationEngine: GeoLocationEngine;
  userNotificationEngine: UserNotificationEngine;

  user_store: ProfileStore;
  pack_store: PackStore;
  group_store: GroupStore;

  constructor(protected options: SessionOptions) {
    super();
    this.id = v4();
    this.token_wrapper = new GoFeralTokenWrapper({
      auth_client: options.auth_client,
      profile_id: options.profile_id,
      logout: () => {
        this.iterateListeners((cb) => cb.logout?.());
      },
      notification_store: options.notification_store,
      refresh_token: () => this.refresh_token,
      set_refresh_token: (token: string) => {
        this.refresh_token = token;
        this.iterateListeners((cb) => cb.refreshTokenUpdated?.());
      }
    });
    this.api_client = new GoFeralApiClient({
      url: APP_ENV.API_SERVER,
      token_wrapper: this.token_wrapper
    });
    this.geoLocationEngine = new GeoLocationEngine({
      session: this
    });
    this.userNotificationEngine = new UserNotificationEngine({
      session: this
    });
    this.pack_store = new PackStore({
      session: this,
      user_store: () => this.user_store
    });
    this.group_store = new GroupStore({
      session: this
    });
    this.user_store = new ProfileStore({
      session: this,
      pack_store: () => this.pack_store,
      group_store: () => this.group_store
    });
  }

  dispose() {
    this.iterateListeners((cb) => cb.dispose?.());
  }

  serialize(): SessionSerialized {
    return {
      refresh_token: this.refresh_token,
      profile: this.user.data ? MeResponse.encode(this.user.data) : null,
      session_id: this.id,
      // Persist the auth-selected profile link id, not the API profile id.
      profile_id: this.options.profile_id.toString()
    };
  }

  protected logoutBecauseError() {
    this.options.notification_store.showNotification(
      new Notification({
        type: NotificationType.ERROR,
        desc: 'Something went wrong, please sign in again.'
      })
    );
    this.iterateListeners((cb) => cb.logout?.());
  }

  deserialize(data: SessionSerialized): boolean {
    if ((jwt.decode(data.refresh_token) as any).version !== REFRESH_TOKEN_VERSION) {
      this.logoutBecauseError();
      return false;
    }

    this.options.profile_id = new ObjectId(data.profile_id);
    this.refresh_token = data.refresh_token;
    this.id = data.session_id;
    this.user = new MyProfileModel({
      api_client: this.api_client,
      id: this.options.profile_id,
      pack_store: this.pack_store,
      group_store: this.group_store,
      logout: () => {
        this.logoutBecauseError();
      }
    });
    if (data.profile) {
      this.user.setData(MeResponse.decode(data.profile));
    }
    return true;
  }

  deactivate() {
    this.iterateListeners((cb) => cb.deactivate?.());
  }

  activate() {
    this.iterateListeners((cb) => cb.activate?.());
  }
}
